'use client'

import { Box, Typography, useTheme, type SxProps, type Theme } from '@mui/material'
import { outfit } from '@/lib/fonts'
import { OpasBrandMark } from './OpasBrandMark'

const ff = outfit.style.fontFamily

export const opasBrandSpinSx = {
  animation: 'opasBrandSpin 1.05s linear infinite',
  '@keyframes opasBrandSpin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
} as const

/** Dönen OPAS mark — buton / satır içi */
export function OpasBrandSpinner({
  size = 22,
  color,
  sx,
}: {
  size?: number
  color?: string
  sx?: SxProps<Theme>
}) {
  return <OpasBrandMark size={size} color={color} sx={{ ...opasBrandSpinSx, ...sx }} />
}

export type OpasBrandLoadingVariant = 'block' | 'page'

/**
 * Ortalanmış OPAS yükleme. Tam sayfa kapı için `variant="page"`,
 * panel / sayfa içi için varsayılan `block`. Düğme içi için `OpasBrandSpinner`.
 */
export function OpasBrandLoading({
  size,
  label,
  variant = 'block',
  color,
  sx,
}: {
  size?: number
  label?: string
  variant?: OpasBrandLoadingVariant
  color?: string
  sx?: SxProps<Theme>
}) {
  const theme = useTheme()
  const isPage = variant === 'page'
  const markSize = size ?? (isPage ? 52 : 40)
  const statusLabel = label ?? 'Yükleniyor'

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-busy
      aria-label={label ? undefined : statusLabel}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: label ? 1.75 : 0,
        py: isPage ? 0 : 3,
        px: 2,
        fontFamily: ff,
        ...(isPage
          ? {
              minHeight: '100vh',
              width: '100%',
              bgcolor: theme.palette.background.default,
            }
          : {
              flex: 1,
              minHeight: 160,
              width: '100%',
            }),
        ...sx,
      }}
    >
      <OpasBrandSpinner size={markSize} color={color} />
      {label ? (
        <Typography
          sx={{
            fontFamily: ff,
            fontSize: '0.82rem',
            fontWeight: 500,
            color: 'text.secondary',
            letterSpacing: '0.02em',
          }}
        >
          {label}
        </Typography>
      ) : null}
    </Box>
  )
}
